import { FormikProps } from "formik";
import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useStore } from "../../../contexts/store-provider";
import { formatMessage } from "../../../translations/format-message";
import toast from "react-hot-toast";
import { formatNumberWithCommas } from "../../../constants/constants";
import { formatTimeDisplay } from "../../../helpers/date-time-helper";

const AddOptionTreatmentForm: React.FC<{
  formikProps: FormikProps<any>;
  addedTreatments: any;
  setSelectedTreatmentId: React.Dispatch<React.SetStateAction<number[]>>;
}> = observer(({ formikProps, addedTreatments, setSelectedTreatmentId }) => {
  const { values, setFieldValue } = formikProps; // Destructure values and setFieldValue from formikProps
  const {
    categoryManagementStore,
    bookingManagementStore,
    masterManagementStore,
  } = useStore(); // Destructure stores from the context
  const { allCategoryBookingManagement } = categoryManagementStore; // Get all categories for booking management
  const { getAllTimeOptions } = masterManagementStore; // Get all time options from master management store
  const { GetTreatmentOptionMenuListByCatIdService, getData } =
    bookingManagementStore; // Get services and data fetching functions from booking management store

  // State to track open/closed status for any node by its id.
  // State to track open/closed status for nodes by their IDs
  const [openNodes, setOpenNodes] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    setOpenNodes({});
  }, values);
  // Toggle the open/close state for a given node ID
  const toggleNode = (nodeId: string) => {
    setOpenNodes((prev) => ({
      ...prev,
      [nodeId]: !prev[nodeId], // Flip the current state of the node
    }));
  };

  const handleOptionServiceSelect = (treatmentId: number, optionId: number) => {
    const selectedServices: any[] = [...values.selectedServices];
    const treatmentObj = selectedServices?.find(
      (item) => item.TreatmentId === treatmentId
    );
    let duplicateOptionId = selectedServices.find((item) =>
      item.OptionIds?.includes(optionId)
    );
    if (treatmentObj) {
      // Check if the optionId already exists in the optionIds array
      const optionIndex = treatmentObj.OptionIds?.indexOf(optionId);
      if (duplicateOptionId && duplicateOptionId?.TreatmentId !== treatmentId) {
        toast.error(formatMessage("option_already_selected"));
      } else {
        if (optionIndex === -1) {
          treatmentObj.OptionIds.push(optionId);
        } else {
          treatmentObj.OptionIds.splice(optionIndex, 1);
        }
      }
    } else {
      selectedServices.push({
        TreatmentId: treatmentId,
        OptionIds: duplicateOptionId ? [] : [optionId],
      });
    }
    setFieldValue("selectedServices", selectedServices);
  };

  // Function to handle selection/deselection of a treatment service
  const handleTreatmentServiceSelect = (treatmentId: number) => {
    const selectedServices: any[] = [
      //...addedTreatments,
      ...values.selectedServices,
    ]; // Clone the selected services array
    const index = selectedServices.findIndex(
      (service: any) => service.TreatmentId == treatmentId // Find the index of the treatment in the selected services
    );
    if (index === -1) {
      setSelectedTreatmentId((prev: number[]) => {
        const tempArray = [...prev];
        tempArray.push(treatmentId);
        return tempArray;
      }); // Set the selected treatment ID
      selectedServices.push({ TreatmentId: treatmentId, OptionIds: [] }); // Add the treatment if not already selected
    } else {
      setSelectedTreatmentId((prev: number[]) => {
        const tempArray = [...prev];
        return tempArray.filter((item: number) => item !== treatmentId);
      });
      selectedServices.splice(index, 1); // Remove the treatment if already selected
    }
    setFieldValue("selectedServices", selectedServices); // Update the selected services in the form state
  };

  // Recursive function to render nested children (services)
  const renderNestedServices = (
    subCategories: any[],
    parentPath: string = ""
  ) => {
    return (
      <div className="px-2 mt-2">
        {subCategories?.map((subCategory: any) => {
          const nodeKey = parentPath
            ? `${parentPath}-${subCategory.id}`
            : `${subCategory.id}`; // Generate unique key for each subcategory
          return (
            <div key={nodeKey}>
              <button
                type="button"
                className="accordian_btn d-flex justify-content-between align-items-center text-start"
                onClick={() => {
                  GetTreatmentOptionMenuListByCatIdService(subCategory.id); // Fetch treatment options for the subcategory
                  toggleNode(nodeKey); // Toggle the visibility of the subcategory
                }}
              >
                {subCategory.title}
                {openNodes[nodeKey] ? (
                  <svg
                    fill="currentColor"
                    height="16"
                    viewBox="0 0 367.676 367.676"
                    width="16"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g>
                      <g>
                        <path d="m344.696 287.246c-5.881 0-11.763-2.243-16.249-6.73l-144.61-144.609-144.608 144.609c-8.973 8.975-23.524 8.975-32.498 0-8.974-8.974-8.974-23.524 0-32.498l160.857-160.859c4.31-4.309 10.154-6.731 16.249-6.731 6.094 0 11.94 2.422 16.25 6.731l160.858 160.858c8.974 8.974 8.974 23.524 0 32.498-4.486 4.488-10.368 6.731-16.249 6.731z"></path>
                      </g>
                    </g>
                  </svg>
                ) : (
                  <svg
                    fill="currentColor"
                    height="16"
                    viewBox="0 0 367.676 367.676"
                    width="16"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g>
                      <g>
                        <path d="m183.837 287.247c-6.095 0-11.939-2.422-16.249-6.731l-160.857-160.859c-8.974-8.975-8.974-23.524 0-32.498 8.975-8.974 23.525-8.973 32.498 0l144.609 144.609 144.609-144.609c8.973-8.974 23.525-8.974 32.498 0 8.974 8.974 8.974 23.524 0 32.498l-160.858 160.859c-4.31 4.309-10.155 6.731-16.25 6.731z"></path>
                      </g>
                    </g>
                  </svg>
                )}
              </button>
              {openNodes[nodeKey] && (
                <div className="px-2 mt-2">
                  {getData(subCategory.id)?.length > 0 ? ( // Check if there are treatments in the subcategory
                    getData(subCategory.id)?.map((treatment: any) => {
                      const treatmentKey = parentPath
                        ? `${parentPath}-${subCategory.id}-${treatment.Id}`
                        : `${subCategory.id}-${treatment.Id}`; // Generate unique key for each treatment
                      return (
                        <div key={treatmentKey}>
                          <div
                            className={`service-item mb-2 ${openNodes[treatment.Id] ? "active" : "" // Highlight active treatment
                              }
                          ${values.selectedServices.some(
                                (service: any) =>
                                  service.TreatmentId == Number(treatment.Id) // Check if treatment is selected
                              )
                                ? "selected"
                                : ""
                              }`}
                          >
                            <div className="d-flex justify-content-between align-items-center">
                              <div className="treatment_selection_name">
                                <h4>{treatment.TreatmentName}</h4>{" "}
                                {/* Display treatment name */}
                                <div className="small text-muted">
                                  <i className="bi bi-currency-yen me-1"></i>{formatNumberWithCommas(treatment.FeeIncludingTax)} ·{" "}
                                  {
                                    formatTimeDisplay(Number(getAllTimeOptions?.find(
                                      (time) => time.id === treatment.Time // Find time value for the treatment
                                    )?.value ?? 0))
                                  }
                                </div>
                                <div className="small text-muted">
                                  {treatment.Description}{" "}
                                  {/* Display treatment description */}
                                </div>
                              </div>
                              {values.selectedServices.some(
                                (service: any) =>
                                  service.TreatmentId == Number(treatment.Id) // Check if treatment is selected
                              ) ? (
                                <div className="text-end">
                                  <span className="bg-primary text-white py-1 px-1 px-md-2 fs-sm rounded me-2">
                                    <svg
                                      className="me-1"
                                      height="12"
                                      viewBox="0 -65 512 512"
                                      width="12"
                                      fill="currentColor"
                                    >
                                      <path d="m444.175781 0-260.871093 242.011719-110.324219-117.734375-72.980469 68.386718 178.234375 190.207032 333.765625-309.351563zm0 0"></path>
                                    </svg>
                                    <FormattedMessage id="menu_added" />
                                  </span>
                                  <button
                                    type="button"
                                    className="btn btn-outline-danger btn-sm"
                                    disabled={addedTreatments.some(
                                      (service: any) =>
                                        service.TreatmentId == treatment.Id
                                    )}
                                    onClick={(e) => {
                                      toggleNode(treatmentKey); // Toggle treatment visibility
                                      e.stopPropagation();
                                      handleTreatmentServiceSelect(
                                        Number(treatment.Id) // Deselect treatment
                                      );
                                    }}
                                  >
                                    <svg
                                      x="0px"
                                      y="0px"
                                      viewBox="0 0 512 512"
                                      fill="currentColor"
                                      width="14"
                                    >
                                      <g>
                                        <g>
                                          <path
                                            d="M436,60h-75V45c0-24.813-20.187-45-45-45H196c-24.813,0-45,20.187-45,45v15H76c-24.813,0-45,20.187-45,45
          c0,19.928,13.025,36.861,31.005,42.761L88.76,470.736C90.687,493.875,110.385,512,133.604,512h244.792
          c23.22,0,42.918-18.125,44.846-41.271l26.753-322.969C467.975,141.861,481,124.928,481,105C481,80.187,460.813,60,436,60z M181,45
          c0-8.271,6.729-15,15-15h120c8.271,0,15,6.729,15,15v15H181V45z M393.344,468.246c-0.643,7.712-7.208,13.754-14.948,13.754
          H133.604c-7.739,0-14.305-6.042-14.946-13.747L92.294,150h327.412L393.344,468.246z M436,120H76c-8.271,0-15-6.729-15-15
          s6.729-15,15-15h360c8.271,0,15,6.729,15,15S444.271,120,436,120z"
                                          ></path>
                                        </g>
                                      </g>
                                      <g>
                                        <g>
                                          <path
                                            d="M195.971,436.071l-15-242c-0.513-8.269-7.67-14.558-15.899-14.043c-8.269,0.513-14.556,7.631-14.044,15.899l15,242.001
          c0.493,7.953,7.097,14.072,14.957,14.072C189.672,452,196.504,444.684,195.971,436.071z"
                                          ></path>
                                        </g>
                                      </g>
                                      <g>
                                        <g>
                                          <path d="M256,180c-8.284,0-15,6.716-15,15v242c0,8.284,6.716,15,15,15s15-6.716,15-15V195C271,186.716,264.284,180,256,180z"></path>
                                        </g>
                                      </g>
                                      <g>
                                        <g>
                                          <path
                                            d="M346.927,180.029c-8.25-0.513-15.387,5.774-15.899,14.043l-15,242c-0.511,8.268,5.776,15.386,14.044,15.899
          c8.273,0.512,15.387-5.778,15.899-14.043l15-242C361.483,187.659,355.196,180.541,346.927,180.029z"
                                          ></path>
                                        </g>
                                      </g>
                                    </svg>
                                  </button>
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  className="btn btn-outline-primary btn-sm"
                                  disabled={addedTreatments.some(
                                    (service: any) =>
                                      service.TreatmentId === treatment.Id
                                  )}
                                  onClick={(e) => {
                                    toggleNode(treatmentKey); // Toggle treatment visibility
                                    e.stopPropagation();
                                    handleTreatmentServiceSelect(
                                      Number(treatment.Id) // Select treatment
                                    );
                                  }}
                                >
                                  + <FormattedMessage id="add_a_menu" />
                                </button>
                              )}
                            </div>
                            {openNodes[treatmentKey] &&
                              treatment.optionVm?.length > 0 && (
                                <div className="options-menu">
                                  {treatment.optionVm?.map(
                                    (option: any, index: number) => (
                                      <React.Fragment>
                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                          <div>{option.OptionName}
                                            <span className="badge bg-primary rounded ms-2">
                                                <FormattedMessage id="option" />
                                            </span>
                                            </div>{" "}
                                          {/* Display option name */}
                                          {values.selectedServices?.some(
                                            (item: any) =>
                                              item.TreatmentId ===
                                              Number(treatment.Id) &&
                                              item.OptionIds?.includes(
                                                Number(option.OptionId)
                                              ) // Check if option is selected
                                          ) ? (
                                            <button
                                              type="button"
                                              key={option.OptionId}
                                              className="btn btn-primary btn-sm"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleOptionServiceSelect(
                                                  Number(treatment.Id), // Select treatment option
                                                  Number(option.OptionId)
                                                );
                                              }}
                                            >
                                              <span>
                                                <i className="bi bi-check-lg me-1"></i>
                                                <FormattedMessage id="options_selected" />
                                              </span>
                                            </button>
                                          ) : (
                                            <button
                                              type="button"
                                              key={option.OptionId}
                                              className="btn btn-outline-primary btn-sm"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleOptionServiceSelect(
                                                  Number(treatment.Id), // Select treatment option
                                                  Number(option.OptionId)
                                                );
                                              }}
                                            >
                                              <span>
                                                <FormattedMessage id="select_option_menu" />
                                              </span>
                                            </button>
                                          )}
                                        </div>
                                      </React.Fragment>
                                    )
                                  )}
                                </div>
                              )}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-center">
                      <FormattedMessage id="no_treatment" />
                    </p>
                  )}
                </div>
              )}
              {subCategory.children &&
                subCategory.children?.length > 0 &&
                renderNestedServices(subCategory.children, nodeKey)}{" "}
              {/* Recursively render nested subcategories */}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <div className="service-selection">
        {/* <MenuSelectionSummary selectedServices={values.selectedServices} /> */}

        {/* Category Dropdowns */}
        <div className="choose-menu-section mb-4 p-2">
          {allCategoryBookingManagement?.length > 0 ? (
            allCategoryBookingManagement?.map((category: any) => {
              if (category.distinction == 1) {
                return (
                  <div key={category.id} className="px-1">
                    {/* Button to toggle category dropdown */}
                    <button
                      type="button"
                      className="accordian_btn d-flex justify-content-between align-items-center text-start"
                      onClick={() => {
                        GetTreatmentOptionMenuListByCatIdService(category.id);
                        toggleNode(`${category.id}-NCT`);
                      }}
                    >
                      {category.title}
                      {openNodes[`${category.id}-NCT`] ? (
                        <svg
                          fill="currentColor"
                          height="16"
                          viewBox="0 0 367.676 367.676"
                          width="16"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <g>
                            <g>
                              <path d="m344.696 287.246c-5.881 0-11.763-2.243-16.249-6.73l-144.61-144.609-144.608 144.609c-8.973 8.975-23.524 8.975-32.498 0-8.974-8.974-8.974-23.524 0-32.498l160.857-160.859c4.31-4.309 10.154-6.731 16.249-6.731 6.094 0 11.94 2.422 16.25 6.731l160.858 160.858c8.974 8.974 8.974 23.524 0 32.498-4.486 4.488-10.368 6.731-16.249 6.731z"></path>
                            </g>
                          </g>
                        </svg>
                      ) : (
                        <svg
                          fill="currentColor"
                          height="16"
                          viewBox="0 0 367.676 367.676"
                          width="16"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <g>
                            <g>
                              <path d="m183.837 287.247c-6.095 0-11.939-2.422-16.249-6.731l-160.857-160.859c-8.974-8.975-8.974-23.524 0-32.498 8.975-8.974 23.525-8.973 32.498 0l144.609 144.609 144.609-144.609c8.973-8.974 23.525-8.974 32.498 0 8.974 8.974 8.974 23.524 0 32.498l-160.858 160.859c-4.31 4.309-10.155 6.731-16.25 6.731z"></path>
                            </g>
                          </g>
                        </svg>
                      )}
                    </button>

                    {openNodes[`${category.id}-NCT`] && (
                      <div className="mt-2">
                        {getData(category.id)?.length > 0 ? (
                          getData(category.id)?.map((treatment) => {
                            const treatmentKey = category.id
                              ? `${category.id}-NCT-${treatment.Id}`
                              : `${category.id}-${treatment.Id}`;

                            return (
                              <div key={treatmentKey}>
                                <div
                                  className={`service-item mb-2 ${openNodes[treatment.Id] ? "active" : ""
                                    }
                              ${values.selectedServices.some(
                                      (service: any) =>
                                        service.TreatmentId == Number(treatment.Id)
                                    )
                                      ? "selected"
                                      : ""
                                    }`}
                                  onClick={() => {
                                    // toggleNode(treatmentKey);
                                  }}
                                >
                                  <div className="d-flex justify-content-between align-items-center">
                                    <div className="treatment_selection_name">
                                      <h4>{treatment.TreatmentName}</h4>
                                      <div className="small text-muted">
                                        <i className="bi bi-currency-yen me-1"></i>{formatNumberWithCommas(treatment.FeeIncludingTax)} ·{" "}
                                        {
                                          formatTimeDisplay(Number(getAllTimeOptions?.find(
                                            (time) => time.id === treatment.Time
                                          )?.value ?? 0))
                                        }
                                      </div>
                                      <div className="small text-muted">
                                        {treatment.Description}
                                      </div>
                                    </div>

                                    {values.selectedServices.some(
                                      (service: any) =>
                                        service.TreatmentId ==
                                        Number(treatment.Id)
                                    ) ? (
                                      <div className="text-end">
                                        {/* Display selected treatment */}
                                        <span className="bg-primary text-white py-1 px-1 px-md-2 fs-sm rounded me-2">
                                          <svg
                                            className="me-1"
                                            height="12"
                                            viewBox="0 -65 512 512"
                                            width="12"
                                            fill="currentColor"
                                          >
                                            <path d="m444.175781 0-260.871093 242.011719-110.324219-117.734375-72.980469 68.386718 178.234375 190.207032 333.765625-309.351563zm0 0"></path>
                                          </svg>
                                          <FormattedMessage id="menu_added" />
                                        </span>
                                        {!addedTreatments.some(
                                          (service: any) =>
                                            service.TreatmentId == treatment.Id
                                        ) && (
                                            <button
                                              type="button"
                                              className="btn btn-outline-danger btn-sm"
                                              onClick={(e) => {
                                                toggleNode(treatmentKey);
                                                e.stopPropagation();
                                                handleTreatmentServiceSelect(
                                                  Number(treatment.Id)
                                                );
                                              }}
                                            >
                                              <svg
                                                x="0px"
                                                y="0px"
                                                viewBox="0 0 512 512"
                                                fill="currentColor"
                                                width="14"
                                              >
                                                <g>
                                                  <g>
                                                    <path
                                                      d="M436,60h-75V45c0-24.813-20.187-45-45-45H196c-24.813,0-45,20.187-45,45v15H76c-24.813,0-45,20.187-45,45
        c0,19.928,13.025,36.861,31.005,42.761L88.76,470.736C90.687,493.875,110.385,512,133.604,512h244.792
        c23.22,0,42.918-18.125,44.846-41.271l26.753-322.969C467.975,141.861,481,124.928,481,105C481,80.187,460.813,60,436,60z M181,45
        c0-8.271,6.729-15,15-15h120c8.271,0,15,6.729,15,15v15H181V45z M393.344,468.246c-0.643,7.712-7.208,13.754-14.948,13.754
        H133.604c-7.739,0-14.305-6.042-14.946-13.747L92.294,150h327.412L393.344,468.246z M436,120H76c-8.271,0-15-6.729-15-15
        s6.729-15,15-15h360c8.271,0,15,6.729,15,15S444.271,120,436,120z"
                                                    ></path>
                                                  </g>
                                                </g>
                                                <g>
                                                  <g>
                                                    <path
                                                      d="M195.971,436.071l-15-242c-0.513-8.269-7.67-14.558-15.899-14.043c-8.269,0.513-14.556,7.631-14.044,15.899l15,242.001
        c0.493,7.953,7.097,14.072,14.957,14.072C189.672,452,196.504,444.684,195.971,436.071z"
                                                    ></path>
                                                  </g>
                                                </g>
                                                <g>
                                                  <g>
                                                    <path d="M256,180c-8.284,0-15,6.716-15,15v242c0,8.284,6.716,15,15,15s15-6.716,15-15V195C271,186.716,264.284,180,256,180z"></path>
                                                  </g>
                                                </g>
                                                <g>
                                                  <g>
                                                    <path
                                                      d="M346.927,180.029c-8.25-0.513-15.387,5.774-15.899,14.043l-15,242c-0.511,8.268,5.776,15.386,14.044,15.899
        c8.273,0.512,15.387-5.778,15.899-14.043l15-242C361.483,187.659,355.196,180.541,346.927,180.029z"
                                                    ></path>
                                                  </g>
                                                </g>
                                              </svg>
                                            </button>
                                          )}
                                      </div>
                                    ) : (
                                      <button
                                        type="button"
                                        className="btn btn-outline-primary btn-sm"
                                        onClick={(e) => {
                                          toggleNode(treatmentKey);
                                          e.stopPropagation();
                                          handleTreatmentServiceSelect(
                                            Number(treatment.Id)
                                          );
                                        }}
                                      >
                                        + <FormattedMessage id="add_a_menu" />
                                      </button>
                                    )}
                                  </div>

                                  {values?.selectedServices?.some(
                                    (service: any) =>
                                      service.TreatmentId ===
                                      Number(treatment.Id)
                                  ) && treatment.optionVm?.length > 0 && (
                                      <div className="options-menu mb-1">
                                        {treatment.optionVm?.map(
                                          (option: any, index: number) => (
                                            <React.Fragment key={option.OptionId}>
                                              <div className="d-flex justify-content-between align-items-center p-2">
                                                <div className="treatment_selection_name">
                                                  {/* <strong>{index + 1}.</strong>{" "} */}
                                                  {option.OptionName}
                                                  <span className="badge bg-primary rounded ms-2">
                                                    <FormattedMessage id="option" />
                                                  </span>
                                                </div>

                                                {values.selectedServices?.some(
                                                  (item: any) =>
                                                    item.TreatmentId ===
                                                    Number(treatment.Id) &&
                                                    item.OptionIds?.includes(
                                                      Number(option.OptionId)
                                                    )
                                                ) ? (
                                                  <button
                                                    type="button"
                                                    key={option.OptionId}
                                                    className="btn btn-primary btn-sm"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      handleOptionServiceSelect(
                                                        Number(treatment.Id),
                                                        Number(option.OptionId)
                                                      );
                                                    }}
                                                  >
                                                    <span>
                                                      <i className="bi bi-check-lg me-1"></i>
                                                      <FormattedMessage id="options_selected" />
                                                    </span>
                                                  </button>
                                                ) : (
                                                  <button
                                                    type="button"
                                                    key={option.OptionId}
                                                    className="btn btn-outline-primary btn-sm"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      handleOptionServiceSelect(
                                                        Number(treatment.Id),
                                                        Number(option.OptionId)
                                                      );
                                                    }}
                                                  >
                                                    <span>
                                                      <FormattedMessage id="select_option_menu" />
                                                    </span>
                                                  </button>
                                                )}
                                              </div>
                                            </React.Fragment>
                                          )
                                        )}
                                      </div>
                                    )}
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          category.children?.length === 0 &&
                          <p className="text-center">
                            <FormattedMessage id="no_treatment" />
                          </p>
                        )}
                      </div>
                    )}

                    {/* Render nested children for this category */}
                    {openNodes[`${category.id}-NCT`] &&
                      category.children &&
                      renderNestedServices(category.children, category.id)}
                  </div>
                );
              }
            })
          ) : (
            <div className="text-center">
              {" "}
              <FormattedMessage id="no_category" />
            </div>
          )}
        </div>
      </div>
    </>
  );
});

export default AddOptionTreatmentForm;
